import { useState } from "react";
import { MessageSquare, Pencil, Trash2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { apiService, extractApiError } from "../services/api";
import { useAuth } from "../hooks/useAuth";
import { canDeleteRoadmapComment, canEditRoadmapComment } from "../types";

interface RoadmapCommentsProps {
  itemId: string;
  teamOwnerEmail: string;
  disabled: boolean;
}

export default function RoadmapComments({
  itemId,
  teamOwnerEmail,
  disabled,
}: RoadmapCommentsProps) {
  const { user } = useAuth();
  const client = useQueryClient();
  const key = ["roadmap-comments", itemId];
  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState<string>();
  const [editBody, setEditBody] = useState("");
  const [editError, setEditError] = useState("");
  const comments = useQuery(key, async () => {
    const { data } = await apiService.getRoadmapComments(itemId);
    if (!data.success || !data.data)
      throw new Error(data.error || "Failed to load project comments");
    return data.data.items;
  });
  const add = useMutation(
    async (text: string) => {
      const { data } = await apiService.addRoadmapComment(itemId, text);
      if (!data.success || !data.data)
        throw new Error(data.error || "Failed to post comment");
      return data.data;
    },
    {
      onSuccess: () => {
        setBody("");
        setError("");
        client.invalidateQueries(key);
        toast.success("Comment posted");
      },
      onError: (err) => {
        toast.error(extractApiError(err));
      },
    },
  );
  const remove = useMutation(
    async (id: string) => {
      const { data } = await apiService.deleteRoadmapComment(itemId, id);
      if (!data.success)
        throw new Error(data.error || "Failed to delete comment");
    },
    {
      onSuccess: () => {
        client.invalidateQueries(key);
        toast.success("Comment deleted");
      },
      onError: (err) => {
        toast.error(extractApiError(err));
      },
    },
  );
  const update = useMutation(
    async ({ id, text }: { id: string; text: string }) => {
      const { data } = await apiService.updateRoadmapComment(itemId, id, text);
      if (!data.success || !data.data)
        throw new Error(data.error || "Failed to update comment");
      return data.data;
    },
    {
      onSuccess: () => {
        setEditingId(undefined);
        setEditError("");
        client.invalidateQueries(key);
        toast.success("Comment updated");
      },
      onError: (err) => {
        toast.error(extractApiError(err));
      },
    },
  );
  const busy =
    disabled || add.isLoading || remove.isLoading || update.isLoading;
  return (
    <section
      aria-label="Comments"
      className="space-y-3 border-b border-gray-200 pb-4 dark:border-gray-700"
    >
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
        <MessageSquare className="h-4 w-4" aria-hidden="true" />
        Comments
      </h3>
      {comments.isLoading && (
        <p role="status" className="text-sm text-gray-500 dark:text-gray-400">
          Loading comments...
        </p>
      )}
      {comments.isError ? (
        <div role="alert" className="text-sm text-red-600 dark:text-red-400">
          Unable to load comments.{" "}
          <button
            type="button"
            className="btn btn-secondary"
            disabled={busy}
            onClick={() => comments.refetch()}
          >
            Retry comments
          </button>
        </div>
      ) : comments.data?.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No comments yet.
        </p>
      ) : (
        <ul className="space-y-3">
          {comments.data?.map((comment) => (
            <li
              key={comment.id}
              className="rounded-lg bg-gray-50 p-3 dark:bg-gray-900"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {comment.authorName.trim() || comment.authorEmail} ·{" "}
                  <time dateTime={comment.createdAt}>
                    {new Date(comment.createdAt).toLocaleString()}
                  </time>
                  {comment.updatedAt !== comment.createdAt && (
                    <span> (edited)</span>
                  )}
                </p>
                <div className="flex items-center gap-1">
                  {canEditRoadmapComment(user, comment) && (
                    <button
                      type="button"
                      aria-label="Edit comment"
                      className="btn-icon"
                      disabled={busy || editingId !== undefined}
                      onClick={() => {
                        setEditingId(comment.id);
                        setEditBody(comment.body);
                        setEditError("");
                      }}
                    >
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                  {canDeleteRoadmapComment(user, comment, teamOwnerEmail) && (
                    <button
                      type="button"
                      aria-label="Delete comment"
                      className="btn-icon"
                      disabled={busy || editingId !== undefined}
                      onClick={() => {
                        if (window.confirm("Delete this comment?"))
                          remove.mutate(comment.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              {editingId === comment.id ? (
                <div className="mt-2 space-y-2">
                  <label className="block">
                    <span className="sr-only">Edit comment</span>
                    <textarea
                      className="input h-auto min-h-16 font-normal text-gray-700 dark:text-gray-300 [field-sizing:content]"
                      rows={2}
                      maxLength={2000}
                      value={editBody}
                      disabled={busy}
                      aria-invalid={Boolean(editError)}
                      aria-describedby={
                        editError ? "roadmap-comment-edit-error" : undefined
                      }
                      onChange={(event) => setEditBody(event.target.value)}
                    />
                  </label>
                  {editError && (
                    <p
                      id="roadmap-comment-edit-error"
                      role="alert"
                      className="text-sm text-red-600 dark:text-red-400"
                    >
                      {editError}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={busy}
                      onClick={() => {
                        const text = editBody.trim();
                        if (!text || Array.from(text).length > 2000) {
                          setEditError("Enter a comment of 1-2000 characters.");
                          return;
                        }
                        setEditError("");
                        update.mutate({ id: comment.id, text });
                      }}
                    >
                      Save comment
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={busy}
                      aria-label="Cancel comment edit"
                      onClick={() => {
                        setEditingId(undefined);
                        setEditError("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-700 dark:text-gray-300">
                  {comment.body}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        <span className="sr-only">New comment</span>
        <textarea
          className="input mt-1 h-auto min-h-16 font-normal text-gray-700 dark:text-gray-300 [field-sizing:content]"
          rows={2}
          maxLength={2000}
          placeholder="Add a comment"
          value={body}
          disabled={busy}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? "roadmap-comment-error" : undefined}
          onChange={(event) => setBody(event.target.value)}
        />
      </label>
      {error && (
        <p
          id="roadmap-comment-error"
          role="alert"
          className="text-sm text-red-600 dark:text-red-400"
        >
          {error}
        </p>
      )}
      <button
        type="button"
        className="btn btn-primary"
        disabled={busy}
        onClick={() => {
          const text = body.trim();
          if (!text || Array.from(text).length > 2000) {
            setError("Enter a comment of 1-2000 characters.");
            return;
          }
          setError("");
          add.mutate(text);
        }}
      >
        Post comment
      </button>
    </section>
  );
}
